import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { readPresentation, shapesOfType } from "@/office/powerpoint/presentation";
import { MAKRO_INTERAKTIV } from "../makros";
import { nichtPruefbar } from "../schnittstelle";

export const lektionMedien: Lesson = {
  id: "medien",
  title: "Video und Audio",
  summary: "Bewegtbild in der Präsentation – das zuverlässig startet, auch auf fremden Geräten.",
  durationMinutes: 9,
  learningGoals: [
    "Video und Audio einbetten statt verknüpfen",
    "Die Wiedergabe auf „Automatisch“ oder „Beim Klicken“ einstellen",
    "Medien komprimieren und mit Untertiteln versehen",
  ],
  steps: [
    {
      id: "video",
      title: "Ein Video einfügen",
      explanation: [
        "„Einfügen“ → „Video“ → „Dieses Gerät“ bettet eine Videodatei in die Präsentation " +
          "ein. Eingebettet heißt: Sie reist mit der Datei, und nichts geht verloren, wenn " +
          "die Präsentation auf einem anderen Rechner läuft. Onlinevideos brauchen dagegen " +
          "beim Vortrag eine Internetverbindung.",
        "Am besten landet das Video in einem Inhaltsplatzhalter – dann sitzt es wie ein " +
          "Bild am richtigen Ort (Modul „Bilder und Formen“).",
      ],
      keyPoints: [
        "Einfügen → Video → Dieses Gerät = eingebettet.",
        "Onlinevideos brauchen Internet beim Vortrag.",
      ],
      task: {
        instruction: "Füge auf einer Folie ein Video oder eine Audiodatei ein.",
        hint: "Einfügen → Video → Dieses Gerät, oder im leeren Inhaltsplatzhalter das Videosymbol.",
        check: async () => {
          const snapshot = await readPresentation();
          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar("Die Art einer Form", "1.4", "Sieh selbst nach, ob das Video auf der Folie liegt.");
          }
          const medien = shapesOfType(snapshot, "Media");
          const imPlatzhalter = snapshot.slides.flatMap((folie) =>
            folie.shapes.filter((form) => form.placeholder?.containedType === "Media"),
          );
          const anzahl = medien.length + imPlatzhalter.length;
          return fromCriteria([criterion(`Video- oder Audioobjekte: ${anzahl}`, anzahl > 0)], {
            success: "Das Video ist eingebettet und reist mit der Datei.",
            failure: "In der Präsentation ist noch kein Video und keine Audiodatei.",
          });
        },
      },
    },
    {
      id: "wiedergabe",
      title: "Die Wiedergabe einstellen",
      explanation: [
        "Die Registerkarte „Wiedergabe“ erscheint, sobald das Video markiert ist. Unter " +
          "„Start“ stehen „Bei Klickfolge“, „Automatisch“ und „Beim Klicken auf“. " +
          "„Automatisch“ startet das Video, sobald die Folie erscheint – kein Suchen nach dem " +
          "Abspielknopf vor Publikum.",
        "Daneben: „Video kürzen“ schneidet Anfang und Ende ab, ohne die Datei zu verändern; " +
          "„Ein-/Ausblenden“ macht den Einstieg weicher. Unter „Datei“ → „Informationen“ → " +
          "„Medien komprimieren“ schrumpft eine große Präsentation deutlich.",
        "Und für die Barrierefreiheit: „Untertitel einfügen“ nimmt eine Untertiteldatei " +
          "(WebVTT) auf – für Gehörlose und für jeden, der das Video ohne Ton sieht.",
      ],
      keyPoints: [
        "Wiedergabe → Start: Automatisch.",
        "Video kürzen statt die Datei bearbeiten.",
        "Datei → Informationen → Medien komprimieren.",
      ],
      task: {
        instruction: "Stelle ein, dass dein Video beim Erscheinen der Folie automatisch startet.",
        hint: "Video anklicken, Registerkarte Wiedergabe → Start: „Automatisch“.",
        macro: MAKRO_INTERAKTIV,
        check: () =>
          pruefeMitMakro(MAKRO_INTERAKTIV, (bericht) =>
            fromCriteria(
              [
                criterion(`Medien in der Präsentation: ${bericht.zahl("medien") ?? 0}`, (bericht.zahl("medien") ?? 0) > 0),
                criterion("Ein Medium startet automatisch", (bericht.zahl("medienAutomatisch") ?? 0) > 0),
              ],
              {
                success: "Das Video startet von selbst – kein Suchen nach dem Knopf vor Publikum.",
                failure: "Das Video startet noch nicht automatisch.",
              },
            ),
          ),
      },
    },
  ],
};
